import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";

export interface EmailRecipient {
  id: string;
  email: string;
  name: string;
  product: "builder" | "engineer";
}

export function AdminComposeEmailDialog({
  recipient,
  onClose,
  replyToMessageId,
  initialSubject = "",
}: {
  recipient: EmailRecipient | null;
  onClose: () => void;
  replyToMessageId?: string;
  initialSubject?: string;
}) {
  const { toast } = useToast();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState("");
  // Keep the same key across retries if a response is lost after Gmail accepted
  // the message. A fresh dialog starts a fresh send attempt.
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const draftContext = useRef("");

  useEffect(() => {
    if (!recipient) return;
    const context = `${recipient.product}:${recipient.id}:${replyToMessageId || ""}`;
    if (draftContext.current === context) return;
    draftContext.current = context;
    setSubject(initialSubject);
    setBody("");
    setError("");
    setUncertain(false);
    setIdempotencyKey(crypto.randomUUID());
  }, [recipient, replyToMessageId, initialSubject]);

  const send = async () => {
    if (!recipient || !subject.trim() || !body.trim() || sending) return;
    setSending(true);
    setError("");
    try {
      const response = await fetch(`/api/admin/users/${encodeURIComponent(recipient.id)}/email`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product: recipient.product,
          subject: subject.trim(),
          body: body.trim(),
          idempotencyKey,
          ...(replyToMessageId ? { replyToMessageId } : {}),
        }),
      });
      const result = await response.json().catch(() => null);
      if (response.status === 202 || (response.status === 409 && result?.pendingMessageId)) {
        setUncertain(true);
        setError(result?.message || "The send outcome is unknown. Check Gmail Sent before composing another email.");
        return;
      }
      if (!response.ok) {
        if (response.status >= 500 && response.status !== 502) setUncertain(true);
        throw new Error(result?.message || "Email could not be sent. Please check Gmail Sent before trying again.");
      }
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/mail"] });
      toast({ title: "Email sent", description: `Sent to ${recipient.email}.` });
      draftContext.current = "";
      setSubject("");
      setBody("");
      setUncertain(false);
      setIdempotencyKey(crypto.randomUUID());
      onClose();
    } catch (err) {
      if (!(err instanceof Error && err.message.includes("Gmail rejected"))) setUncertain(true);
      setError(err instanceof Error ? err.message : "Email could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!recipient} onOpenChange={(open) => { if (!open && !sending) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{replyToMessageId ? "Reply by email" : "Email user"}</DialogTitle>
          <DialogDescription>
            This email will be sent to {recipient?.name || "this user"} ({recipient?.email}) and recorded in Sent.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); void send(); }} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="admin-email-subject" className="text-sm font-medium">Subject</label>
            <Input
              id="admin-email-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              maxLength={180}
              required
              disabled={uncertain}
              autoFocus
              data-testid="input-admin-email-subject"
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="admin-email-body" className="text-sm font-medium">Message</label>
            <Textarea
              id="admin-email-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={10000}
              rows={8}
              required
              disabled={uncertain}
              className="min-h-36"
              data-testid="input-admin-email-body"
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {uncertain && <p className="text-xs text-muted-foreground">This attempt keeps the same send ID when you check its status. Do not start another message until you have checked Gmail Sent.</p>}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={sending} onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={sending || !subject.trim() || !body.trim()} data-testid="button-send-admin-email">
              <Send className="mr-2 h-4 w-4" />
              {sending ? "Sending…" : uncertain ? "Check send status" : "Send email"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}